import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../config';
import useVoiceChat from './useVoiceChat';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';

const AIChatPanel = ({ contextPetId = null, contextType = 'dashboard', onRefresh = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickActions, setQuickActions] = useState([]);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load quick actions on mount
  useEffect(() => {
    axios.get(`${API}/ai/quick-actions`)
      .then(res => setQuickActions(res.data.quick_actions || []))
      .catch(err => console.error('Failed to load quick actions:', err));
  }, []);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async (text, displayText = null) => {
    if (!text.trim() || isLoading) return;

    // Show the displayText (short label) in the bubble, but send the full text to the AI
    const userMsg = { role: 'user', text: displayText || text.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsLoading(true);
    setShowQuickActions(false);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: text.trim(),
        session_id: sessionId,
        context_type: contextType,
        context_pet_id: contextPetId || null
      });

      const aiMsg = {
        role: 'ai',
        text: res.data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMsg]);
      setSessionId(res.data.session_id);

      // If the AI executed a write action (e.g. location update), refresh the parent data
      if (res.data.actions_executed && onRefresh) {
        onRefresh();
      }
    } catch (err) {
      const errorMsg = {
        role: 'ai',
        text: '❌ Sorry, I had trouble connecting. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    // Show the short label in the chat bubble, send the full message to the AI
    const displayText = action.display_text || action.label;
    sendMessage(action.message, displayText);
  };

  // ---- Voice chat (push-to-talk + hands-free conversation mode) ----
  const sendVoiceBlob = async (blob, mime) => {
    const ext = mime.includes('mp4') ? 'mp4' : 'webm';
    const form = new FormData();
    form.append('audio', blob, `voice.${ext}`);
    if (sessionId) form.append('session_id', sessionId);
    form.append('context_type', contextType);
    if (contextPetId) form.append('context_pet_id', contextPetId);
    const res = await axios.post(`${API}/ai/voice/chat`, form);
    const d = res.data;
    if (d.empty || !d.transcript) return { audioBase64: null };
    setMessages(prev => [...prev,
      { role: 'user', text: `🎤 ${d.transcript}`, timestamp: new Date() },
      { role: 'ai', text: d.response, timestamp: new Date() }
    ]);
    setSessionId(d.session_id);
    setShowQuickActions(false);
    if (d.actions_executed && onRefresh) onRefresh();
    return { audioBase64: d.audio_base64 };
  };

  const handleVoiceError = (kind) => {
    setMessages(prev => [...prev, {
      role: 'ai',
      text: kind === 'mic'
        ? "🎤 I couldn't access your microphone. Please allow mic permission in your browser and try again."
        : kind === 'playback'
        ? "🔇 I couldn't play the voice reply — your browser blocked audio playback. The text answer is above."
        : '❌ Sorry, I had trouble processing your voice. Please try again.',
      timestamp: new Date(),
      isError: true
    }]);
  };

  const {
    voiceState, conversationMode, toggleConversationMode,
    startPushToTalk, stopPushToTalk, stopSpeaking
  } = useVoiceChat({ onSendAudio: sendVoiceBlob, onError: handleVoiceError });

  const handleMicTap = () => {
    if (voiceState === 'recording') stopPushToTalk();
    else if (voiceState === 'idle') startPushToTalk();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputText);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowQuickActions(true);
    setInputText('');
  };

  const formatAIResponse = (text) => {
    // Convert markdown-style formatting to JSX
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('# ')) {
        return <h3 key={i} className="font-bold text-gray-900 text-sm mt-2 mb-1">{line.slice(2)}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h4 key={i} className="font-semibold text-gray-800 text-sm mt-2 mb-1">{line.slice(3)}</h4>;
      }
      if (line.startsWith('• ') || line.startsWith('- ')) {
        return <div key={i} className="flex items-start gap-1 my-0.5"><span className="text-teal-500 mt-0.5 flex-shrink-0">•</span><span>{line.slice(2)}</span></div>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-gray-900 text-sm">{line.slice(2, -2)}</p>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-1.5" />;
      }
      // Handle inline bold **text**
      const boldParts = line.split(/\*\*(.*?)\*\*/g);
      if (boldParts.length > 1) {
        return (
          <p key={i} className="text-sm leading-relaxed">
            {boldParts.map((part, j) =>
              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
            )}
          </p>
        );
      }
      return <p key={i} className="text-sm leading-relaxed">{line}</p>;
    });
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen
            ? 'bg-gray-700 hover:bg-gray-800'
            : 'bg-gradient-to-br from-teal-500 to-blue-600 hover:from-teal-400 hover:to-blue-500'
        }`}
        title={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
      >
        {isOpen ? (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <span className="text-2xl">🧠</span>
        )}
        {/* Pulse indicator when closed */}
        {!isOpen && (
          <span className="absolute top-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ height: '600px', maxHeight: 'calc(100vh - 8rem)' }}>

          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-blue-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xl">🧠</span>
              <div>
                <h3 className="text-white font-semibold text-sm">HOLONS AI Assistant</h3>
                <p className="text-teal-200 text-xs">Live CRM data • Always up to date</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="conversation-mode-toggle"
                onClick={toggleConversationMode}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  conversationMode
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'text-teal-200 hover:text-white hover:bg-white/20'
                }`}
                title={conversationMode ? 'End hands-free conversation' : 'Start hands-free conversation mode'}
              >
                {conversationMode ? '🎙️ End' : '🎙️ Hands-free'}
              </button>
              {messages.length > 0 && (
                <button
                  onClick={clearChat}
                  className="text-teal-200 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/20 transition-colors"
                  title="Clear chat"
                >
                  🗑️ Clear
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-teal-200 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">

            {/* Welcome message */}
            {messages.length === 0 && (
              <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                <p className="text-sm text-gray-700 leading-relaxed">
                  👋 Hi! I have live access to your CRM data. Ask me anything or use the quick actions below.
                </p>
                {contextPetId && (
                  <p className="text-xs text-teal-600 mt-1.5 font-medium">
                    📋 I can see the current pet's details — just ask!
                  </p>
                )}
              </div>
            )}

            {/* Quick Actions */}
            {showQuickActions && quickActions.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2 px-1">⚡ Quick Actions</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {quickActions.map(action => (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      disabled={isLoading}
                      className="text-left text-xs bg-white border border-gray-200 rounded-lg px-2.5 py-2 hover:bg-teal-50 hover:border-teal-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2.5 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-teal-500 to-blue-600 text-white rounded-br-sm'
                    : msg.isError
                    ? 'bg-red-50 border border-red-200 text-red-700 rounded-bl-sm'
                    : 'bg-white border border-gray-100 shadow-sm rounded-bl-sm'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  ) : (
                    <div className="text-gray-800 text-sm leading-relaxed">
                      {formatAIResponse(msg.text)}
                    </div>
                  )}
                  <p className={`text-xs mt-1.5 ${msg.role === 'user' ? 'text-teal-100 text-right' : 'text-gray-400'}`}>
                    {msg.timestamp.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-gray-400">Checking CRM data...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Show quick actions toggle (when hidden) */}
          {!showQuickActions && messages.length > 0 && (
            <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => setShowQuickActions(true)}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium"
              >
                ⚡ Show quick actions
              </button>
            </div>
          )}

          {/* Voice status banner */}
          {(conversationMode || voiceState !== 'idle') && (
            <div data-testid="voice-status-banner" className={`px-3 py-2 border-t flex items-center justify-between flex-shrink-0 text-xs font-medium ${
              voiceState === 'listening' ? 'bg-teal-50 border-teal-100 text-teal-700' :
              voiceState === 'recording' ? 'bg-red-50 border-red-100 text-red-600' :
              voiceState === 'processing' ? 'bg-amber-50 border-amber-100 text-amber-700' :
              voiceState === 'speaking' ? 'bg-blue-50 border-blue-100 text-blue-700' :
              'bg-gray-50 border-gray-100 text-gray-500'
            }`}>
              <span className="flex items-center gap-1.5">
                {voiceState === 'listening' && (<><span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" />Listening — just speak…</>)}
                {voiceState === 'recording' && (<><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />Recording — tap mic to send</>)}
                {voiceState === 'processing' && (<><span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" />Transcribing & thinking…</>)}
                {voiceState === 'speaking' && (<><span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />Speaking…</>)}
                {voiceState === 'idle' && conversationMode && 'Hands-free ready'}
              </span>
              {voiceState === 'speaking' && (
                <button data-testid="voice-stop-speaking-btn" onClick={stopSpeaking} className="text-blue-600 hover:text-blue-800 underline">
                  Stop
                </button>
              )}
            </div>
          )}

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-gray-200 flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about your cases..."
                rows={1}
                disabled={isLoading}
                className="flex-1 resize-none border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400 min-h-[40px] max-h-[100px]"
                style={{ overflowY: 'auto' }}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                }}
              />
              <button
                data-testid="voice-mic-button"
                onClick={handleMicTap}
                disabled={isLoading || conversationMode || voiceState === 'processing' || voiceState === 'speaking'}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 shadow-md ${
                  voiceState === 'recording'
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                    : 'bg-gray-100 hover:bg-teal-50 text-gray-600 hover:text-teal-600 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                title={voiceState === 'recording' ? 'Tap to send' : 'Push to talk'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                </svg>
              </button>
              <button
                onClick={() => sendMessage(inputText)}
                disabled={!inputText.trim() || isLoading}
                className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 hover:from-teal-400 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl flex items-center justify-center transition-all flex-shrink-0 shadow-md"
                title="Send (Enter)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatPanel;
